import { PLAN_FACTS, planName } from '@/shared/config/plans';
import { formatCount, formatMoney, formatMonthDay } from '@/shared/lib/format';
import { summarizeUsage } from '@/shared/lib/pod-usage';
import type { PaymentMethodData, PlanId, SubscriptionData, WorkspaceData } from '../types';

type PillTone = 'ok' | 'info' | 'warn' | 'off' | 'bad';

export function planLines(plan: PlanId): string[] {
  const facts = PLAN_FACTS[plan];
  if (plan === 'FREE') return ['3 Pod-hours, once', '1 Pod at a time', 'No card needed'];
  return [
    `${formatCount(facts.includedPodHours)} Pod-hours a month`,
    `Up to ${facts.podsAtATime} Pods at a time`,
    `${formatMoney(facts.extraPodHourCents ?? 0)} per extra Pod-hour`,
  ];
}

export function planPrice(plan: PlanId): string {
  const { priceCents } = PLAN_FACTS[plan];
  return priceCents === 0 ? '$0' : `${formatMoney(priceCents, 'USD', true)} a month`;
}

export function planStatus(subscription: SubscriptionData | null): {
  tone: PillTone;
  text: string;
} {
  if (!subscription) return { tone: 'off', text: 'Current plan' };
  if (subscription.status === 'PAST_DUE') return { tone: 'bad', text: 'Payment failed' };
  if (subscription.endsAt)
    return { tone: 'warn', text: `Ends ${formatMonthDay(subscription.endsAt)}` };
  const renews = subscription.nextBilledAt ?? subscription.currentPeriodEndsAt;
  return { tone: 'ok', text: renews ? `Renews ${formatMonthDay(renews)}` : 'Active' };
}

export function usageNote(workspace: WorkspaceData): string {
  const usage = summarizeUsage(workspace.usage);
  if (usage.isFree) {
    return usage.isOut
      ? 'All used. Free hours are given once; pick a plan so new sessions keep getting Pods.'
      : `${usage.left} left. Free hours are given once; pick a plan before they run out.`;
  }
  const extraCents = PLAN_FACTS[workspace.plan].extraPodHourCents ?? 0;
  const resets = usage.resetsOn ? `, resets ${usage.resetsOn}` : '';
  const extraSoFar =
    workspace.usage.extraPodHours > 0
      ? `${usage.extra}, ${formatMoney(workspace.usage.extraCostCents)}`
      : '0';
  return `${usage.left} left${resets}. Extra Pod-hours so far: ${extraSoFar}. Extra hours go on the next invoice at ${formatMoney(extraCents)} each.`;
}

export const paymentMethodLabel = (method: PaymentMethodData | null): string =>
  method ? `${method.brand} ending ${method.last4}` : 'No card on file';

/** The day a failed renewal has to be fixed by (Paddle keeps the plan on until then). */
export function pastDueDeadline(subscription: SubscriptionData): string | null {
  const date = subscription.currentPeriodEndsAt ?? subscription.nextBilledAt;
  return date ? formatMonthDay(date) : null;
}

export const isPaidPlan = (plan: PlanId): boolean => plan !== 'FREE';

export const subscriptionPlanName = (subscription: SubscriptionData | null, plan: PlanId): string =>
  planName(subscription?.plan ?? plan);
